import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Flag } from 'lucide-react';

export function GuideContent({ guide }) {
  if (!guide) return null;
  return (
    <div className="space-y-3">
      {(guide.opener || []).length > 0 && (
        <ul className="space-y-1">
          {guide.opener.map((b, i) => (
            <li key={i} className="text-sm text-foreground/90 leading-snug flex gap-2" data-testid={`guide-opener-${i}`}>
              <span className="text-violet-400 shrink-0">•</span>{b}
            </li>
          ))}
        </ul>
      )}
      {(guide.questions || []).length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground font-mono uppercase mb-1.5">Leitfragen · Zielkorridor</p>
          <ol className="space-y-2">
            {guide.questions.map((it, i) => (
              <li key={i} className="flex gap-2 items-start" data-testid={`guide-question-${i}`}>
                <span className="text-violet-400 text-sm shrink-0">{i + 1}.</span>
                <div className="min-w-0">
                  <p className="text-sm text-foreground/90 leading-snug">{it.q}</p>
                  {it.tag && (
                    <span className={`inline-block mt-0.5 text-[9px] font-mono border rounded px-1.5 py-0.5 ${
                      it.tag.startsWith('K.O.')
                        ? 'border-red-500/30 bg-red-500/10 text-red-300'
                        : 'border-border bg-secondary/50 text-muted-foreground'}`}>
                      {it.tag}
                    </span>
                  )}
                  {it.a && (
                    <p className="mt-1 text-xs text-emerald-300/90 leading-snug bg-emerald-500/5 border border-emerald-500/20 rounded-sm px-2 py-1"
                      data-testid={`guide-answer-${i}`}>
                      <span className="font-semibold">Laut Teaser:</span> {it.a}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {guide.next_step && (
        <p className="text-xs text-emerald-300/90 leading-snug flex gap-1.5 items-start" data-testid="guide-next-step">
          <Flag className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {guide.next_step}
        </p>
      )}
    </div>
  );
}

export function CallGuideCard({ projectId }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setGuide(null); setChecked(false);
    api.getCallGuide(projectId)
      .then(r => setGuide(r.data.guide))
      .catch(() => {})
      .finally(() => setChecked(true));
  }, [projectId]);

  const generate = async (force) => {
    setLoading(true);
    try {
      const r = await api.inboundCallBullets(projectId, force);
      setGuide({ opener: r.data.opener || [], questions: r.data.questions || [], next_step: r.data.next_step || '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Leitfaden konnte nicht erstellt werden');
    }
    setLoading(false);
  };

  return (
    <div className="bg-card border border-violet-500/30 rounded-sm p-4" data-testid="call-guide-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-violet-300/90 font-mono uppercase flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Gesprächsleitfaden · Zielkorridor-Check
        </p>
        {guide && (
          <button onClick={() => generate(true)} disabled={loading} data-testid="call-guide-regen"
            className="p-1 text-muted-foreground/60 hover:text-violet-300 transition-colors" title="Neu generieren">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
      {loading ? (
        <div className="space-y-2" data-testid="call-guide-loading">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-3.5 bg-secondary/80 rounded animate-pulse" style={{ width: `${88 - i * 9}%` }} />
          ))}
        </div>
      ) : guide ? (
        <GuideContent guide={guide} />
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            KI-Leitfragen fürs nächste Owner-/Broker-Gespräch — priorisiert nach den offenen Punkten der Investment-Scorecard.
          </p>
          <button onClick={() => generate(false)} disabled={loading || !checked} data-testid="call-guide-generate"
            className="shrink-0 flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-sm border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 transition-colors disabled:opacity-50">
            <Sparkles className="w-3 h-3" /> Leitfaden generieren
          </button>
        </div>
      )}
    </div>
  );
}
